// System
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

// Components
import { StringTableColumnHeader, TableColumnHeader } from '@Components/shared/tables/columns';
import { useEllipsis } from '@Components/shared/styles/useEllipsis';

// Contracts
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';
import { Filterable } from '@contracts/common/traits/Filterable';
import { FailedSendCreditTrasnactionItemDto } from '@contracts/api/dto/failedSendCreditTransaction/FailedSendCreditTransactionItemDto';
// Utils
import {
  FailedSendCreditTransactionsListColumns,
  FailedSendCreditTransactionsListFilter,
  failedSendCreditTransactionsListColumns,
} from '@utils/tables/failedSendCreditTransactions/failedSendCreditTransactionsListFilter';

// Features
import TruncateText from '@Components/truncate-text/TruncateText';
import { DateLabel } from '@Components/shared/labels/DateLabel';
import { parseISO } from 'date-fns';
import { useFailedSendCreditTransactionsTable } from '@Modal/hooks/failedSendCreditTransactionsTable.ts/useFailedSendCreditTransactionsTable';

type HeaderOptions = {
  filter: Filterable<FailedSendCreditTransactionsListFilter>;
};

type Columns = TableColumnDescriptionArray<
  FailedSendCreditTrasnactionItemDto,
  HeaderOptions,
  typeof failedSendCreditTransactionsListColumns
>;

type Column = TableColumnDescription<
  FailedSendCreditTrasnactionItemDto,
  HeaderOptions,
  FailedSendCreditTransactionsListColumns
>;

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

export const useFailedSendCreditTransactionsTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();
  const { handleFilterSelect } = useFailedSendCreditTransactionsTable();

  return useMemo(
    (): Column[] =>
      [
        {
          name: 'failedSendCreditTransaction._id',
          title: 'ID',
          sx: { minWidth: '120px', maxWidth: '200px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'_id'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>{row?._id && <TruncateText text={row._id} maxLength={8} textStyles={styles.id} />}</>
          ),
        },
        {
          name: 'failedSendCreditTransaction.createdAt',
          title: 'Created At',
          sx: { minWidth: '140px', maxWidth: '220px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.createdAt && (
                <DateLabel date={new Date(parseISO(row?.createdAt))} format="long" />
              )}
            </Box>
          ),
        },
        {
          name: 'failedSendCreditTransaction.category',
          title: 'Category',
          sx: { minWidth: '120px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.category}</Box>,
        },
        {
          name: 'failedSendCreditTransaction.language',
          title: 'Language',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.language}</Box>,
        },
        {
          name: 'failedSendCreditTransaction.currency',
          title: 'Currency',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.currency}</Box>,
        },
        {
          name: 'failedSendCreditTransaction.roundId',
          title: 'Round ID',
          sx: { minWidth: '120px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'roundId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>
              {row?.roundId && (
                <TruncateText text={row.roundId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'failedSendCreditTransaction.transactionId',
          title: 'Transaction ID',
          sx: { minWidth: '120px', maxWidth: '200px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'transactionId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>
              {row?.transactionId && (
                <TruncateText text={row.transactionId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'failedSendCreditTransaction.operation',
          title: 'Operation',
          sx: { minWidth: '100px', maxWidth: '200px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.operation}</Box>,
        },
        {
          name: 'failedSendCreditTransaction.reason',
          title: 'Reason',
          sx: { minWidth: '120px', maxWidth: '220px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.reason}</Box>,
        },
        {
          name: 'failedSendCreditTransaction.providerId',
          title: 'Provider ID',
          sx: { minWidth: '120px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'providerId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>
              {row?.providerId && (
                <TruncateText text={row.providerId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'failedSendCreditTransaction.playerId',
          title: 'Player ID',
          sx: { minWidth: '120px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'playerId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>
              {row?.playerId && (
                <TruncateText text={row.playerId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'failedSendCreditTransaction.amount',
          title: 'Amount',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.amount}</Box>,
        },
        {
          name: 'failedSendCreditTransaction.multiplier',
          title: 'Multiplier',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.multiplier}</Box>,
        },
        {
          name: 'failedSendCreditTransaction.gameId',
          title: 'Game ID',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.gameId}</Box>,
        },
        {
          name: 'failedSendCreditTransaction.previousTransactionId',
          title: 'Previous Transaction ID',
          sx: { minWidth: '120px', maxWidth: '220px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'previousTransactionId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>
              {row?.previousTransactionId && (
                <TruncateText
                  text={row.previousTransactionId}
                  maxLength={8}
                  textStyles={styles.id}
                />
              )}
            </>
          ),
        },
        {
          name: 'failedSendCreditTransaction.awardId',
          title: 'Award ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'awardId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>
              {row?.awardId && (
                <TruncateText text={row.awardId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'failedSendCreditTransaction.errorMessage',
          title: 'Error Message',
          sx: { minWidth: '150px', maxWidth: '300px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.errorMessage}</Box>,
        },
        {
          name: 'failedSendCreditTransaction.isResolved',
          title: 'Is Resolved',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.isResolved ? 'Yes' : 'No'}</Box>,
        },
      ] satisfies Columns,
    [ellipsis, styles.id]
  );
};
